import type { FilterSpecEntry } from './types'
import type { RenderContext } from './context'
import { FilterSection, OptionCheckbox, bucketIndex, mergeValues } from './FacetCheckboxGroup'
import { PriceRangeCustom } from './PriceRangeCustom'
import { CategorySection } from './CategorySection'
import {
  STOCK_STATUS_LABEL,
  DELIVERY_SPEED_LABEL,
  LIFECYCLE_LABEL,
} from './labels'
import type { FacetBucket } from './types'

// ─── Hide-empty rule ──────────────────────────────────────────────────────────
// A section is "meaningful" when it has at least 2 selectable (non-zero) options.
// If the section has fewer than 2 selectable options AND the user currently has
// no selection in it, we hide it entirely — a one-option or zero-option filter
// cannot narrow anything.
//
// `hasSelection` must be true when the user already selected a value in this
// section, so they can still uncheck it.

function hasMeaningfulOptions(
  values: string[],
  counts: Map<string, number>,
  facetsLoaded: boolean,
): boolean {
  if (!facetsLoaded) return true // loading — keep visible, don't flash-hide
  const selectable = values.filter((v) => (counts.get(v) ?? 0) > 0)
  return selectable.length >= 2
}

export function renderSection(entry: FilterSpecEntry, ctx: RenderContext) {
  switch (entry.kind) {
    case 'category':
      return <CategorySection key="category" ctx={ctx} />
    case 'subcategory':
      // Subcategory is rendered inline inside the category tree — listing it
      // as its own section would duplicate the tree.
      return null
    case 'series':
      // Series is now rendered as nested subcategories inside the category tree.
      // Rendering a standalone top-level series section would duplicate that tree.
      return null
    case 'price_range':
      return <PriceRangeSection key="price_range" ctx={ctx} />
    case 'stock_status':
      return <StockStatusSection key="stock_status" ctx={ctx} />
    case 'delivery_speed':
      return <DeliverySpeedSection key="delivery_speed" ctx={ctx} />
    case 'brand':
      return <BrandSection key="brand" ctx={ctx} />
    case 'lifecycle_phase':
      return <LifecycleSection key="lifecycle_phase" ctx={ctx} />
    case 'legacy':
      return <LegacySection key="legacy" ctx={ctx} />
    case 'labels':
      return <LabelsSection key="labels" ctx={ctx} />
    case 'attribute':
      return (
        <AttributeSection
          key={`attribute:${entry.attributeKey}`}
          attributeKey={entry.attributeKey}
          label={entry.label}
          ctx={ctx}
        />
      )
    default: {
      const _exhaustive: never = entry
      return _exhaustive
    }
  }
}

function AttributeSection({
  attributeKey,
  label,
  ctx,
}: {
  attributeKey: string
  label: string
  ctx: RenderContext
}) {
  const { facets, attributes, toggleAttributeValue } = ctx
  const buckets = facets?.attribute_facets?.[attributeKey] ?? []
  const selected = attributes[attributeKey] ?? []

  // Render even when there's no facet data yet, IF the user already has a
  // selection — otherwise they couldn't un-check it. Same logic as
  // LabelsSection.
  if (buckets.length === 0 && selected.length === 0) return null

  const counts = bucketIndex(buckets)
  // Always render currently-selected values, even when they aren't in the
  // current facet response (zero-count, but the user needs to un-check).
  const extraSelected = selected.filter((v) => !buckets.some((b) => b.value === v))
  const valuesToRender: FacetBucket[] = [
    ...buckets,
    ...extraSelected.map((value) => ({ value, count: 0 })),
  ]

  return (
    <FilterSection title={label}>
      <div className="flex flex-col gap-1.5">
        {valuesToRender.map((b) => {
          const checked = selected.includes(b.value)
          const count = facets ? (counts.get(b.value) ?? 0) : undefined
          const disabled = facets ? count === 0 && !checked : false
          return (
            <OptionCheckbox
              key={b.value}
              label={b.value}
              count={count}
              checked={checked}
              disabled={disabled}
              onToggle={() => toggleAttributeValue(attributeKey, b.value)}
            />
          )
        })}
      </div>
    </FilterSection>
  )
}

function PriceRangeSection({ ctx }: { ctx: RenderContext }) {
  const { facets, priceRange, setParam } = ctx
  const buckets = facets?.price_buckets ?? []

  function encode(b: { min: number; max: number | null }): string {
    if (b.max === null) return `${Math.round(b.min)}+`
    return `${Math.round(b.min)}-${Math.round(b.max)}`
  }

  return (
    <FilterSection title="Prijs" defaultOpen>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => setParam('price_range', '')}
          className={`text-sm py-1 px-2 rounded text-left w-full ${priceRange === '' ? 'bg-primary text-white font-medium' : 'text-neutral-600 hover:bg-neutral-100'}`}
        >
          Alles
        </button>
        {buckets.map((b) => {
          const encoded = encode(b)
          const active = priceRange === encoded
          const disabled = b.count === 0 && !active
          return (
            <button
              key={b.label}
              disabled={disabled}
              onClick={() => setParam('price_range', active ? '' : encoded)}
              className={`text-sm py-1 px-2 rounded text-left w-full flex justify-between ${active ? 'bg-primary text-white font-medium' : disabled ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-600 hover:bg-neutral-100'}`}
            >
              <span>{b.label}</span>
              <span className={active ? 'text-white' : 'text-neutral-600'}>{b.count}</span>
            </button>
          )
        })}
      </div>
      <PriceRangeCustom value={priceRange} onApply={(v) => setParam('price_range', v)} />
    </FilterSection>
  )
}

function StockStatusSection({ ctx }: { ctx: RenderContext }) {
  const { facets, selectedStockStatuses, toggleCsv } = ctx
  const counts = bucketIndex(facets?.stock_statuses)
  // Exclude 'unknown' from the filter options — it's a data-not-yet-available state,
  // not a meaningful filter choice. The StockBadge hides itself on unknown
  // (WEB-392); selecting it as a filter would just show everything.
  const filterableStatuses = Object.keys(STOCK_STATUS_LABEL).filter((s) => s !== 'unknown')
  const valuesToRender = mergeValues(filterableStatuses, facets?.stock_statuses?.filter((b) => b.value !== 'unknown'))

  // Hide if fewer than 2 selectable options and no active selection
  const hasSelection = selectedStockStatuses.length > 0
  if (!hasMeaningfulOptions(valuesToRender, counts, !!facets) && !hasSelection) return null

  return (
    <FilterSection title="Beschikbaarheid" defaultOpen>
      <div className="flex flex-col gap-1.5">
        {valuesToRender.map((value) => {
          const label = STOCK_STATUS_LABEL[value] ?? value
          const count = facets ? (counts.get(value) ?? 0) : undefined
          const checked = selectedStockStatuses.includes(value)
          const disabled = facets ? count === 0 && !checked : false
          return (
            <OptionCheckbox
              key={value}
              label={label}
              count={count}
              checked={checked}
              disabled={disabled}
              onToggle={() => toggleCsv('stock_status', value)}
            />
          )
        })}
      </div>
    </FilterSection>
  )
}

function DeliverySpeedSection({ ctx }: { ctx: RenderContext }) {
  const { facets, deliverySpeed, setParam } = ctx
  const counts = bucketIndex(facets?.delivery_speeds)
  // Exclude 'unknown' — when all products are unknown, the section is all-zero
  // and useless. Only show the known delivery windows (WEB-392, DEC-022).
  const filterableSpeeds = ['days_3', 'week_1', 'weeks_2_3', 'weeks_3_4']
  const valuesToRender = mergeValues(filterableSpeeds, facets?.delivery_speeds?.filter((b) => b.value !== 'unknown'))

  // Hide if fewer than 2 selectable options and no active selection
  const hasSelection = deliverySpeed !== ''
  if (!hasMeaningfulOptions(valuesToRender, counts, !!facets) && !hasSelection) return null

  return (
    <FilterSection title="Levertijd">
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
          <input
            type="radio"
            name="delivery_speed"
            value=""
            checked={deliverySpeed === ''}
            onChange={() => setParam('delivery_speed', '')}
            className="h-4 w-4 text-primary focus:ring-primary"
          />
          Alle
        </label>
        {valuesToRender.map((value) => {
          const label = DELIVERY_SPEED_LABEL[value] ?? value
          const count = facets ? (counts.get(value) ?? 0) : undefined
          const checked = deliverySpeed === value
          const disabled = facets ? count === 0 && !checked : false
          const inputId = `delivery-speed-${value}`
          return (
            <div
              key={value}
              className={`flex items-center justify-between gap-2 text-sm ${disabled ? 'text-neutral-300' : 'text-neutral-700'}`}
            >
              <label
                htmlFor={inputId}
                className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="delivery_speed"
                  value={value}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => setParam('delivery_speed', value)}
                  className="h-4 w-4 text-primary focus:ring-primary"
                />
                {label}
              </label>
              {count !== undefined && (
                <span className="text-xs text-neutral-600">{count}</span>
              )}
            </div>
          )
        })}
      </div>
    </FilterSection>
  )
}

function BrandSection({ ctx }: { ctx: RenderContext }) {
  const { facets, brand, setParam } = ctx
  const buckets = facets?.brands ?? []
  const fallback = !facets && !brand ? [{ value: 'B&R', count: 0 }] : []
  const entries: FacetBucket[] = buckets.length > 0 ? buckets : fallback

  // Hide when there's only one brand (or zero) and no active selection —
  // a single-brand filter provides no narrowing ability.
  const hasSelection = !!brand
  const selectableCount = entries.filter((b) => b.count > 0).length
  if (facets && selectableCount < 2 && !hasSelection) return null

  return (
    <FilterSection title="Merk">
      <div className="flex flex-col gap-1.5">
        {entries.map((b) => {
          const checked = brand === b.value
          const disabled = facets ? b.count === 0 && !checked : false
          return (
            <OptionCheckbox
              key={b.value}
              label={b.value}
              count={facets ? b.count : undefined}
              checked={checked}
              disabled={disabled}
              onToggle={() => setParam('brand', checked ? '' : b.value)}
            />
          )
        })}
      </div>
    </FilterSection>
  )
}

function LifecycleSection({ ctx }: { ctx: RenderContext }) {
  const { facets, lifecyclePhases, toggleMulti } = ctx
  const counts = bucketIndex(facets?.lifecycle_phases)
  // Exclude 'unknown' — when all products have unknown lifecycle, the section
  // is all-zero and cannot narrow anything.
  const filterablePhases = Object.keys(LIFECYCLE_LABEL).filter((p) => p !== 'unknown')
  const valuesToRender = mergeValues(filterablePhases, facets?.lifecycle_phases?.filter((b) => b.value !== 'unknown'))

  // Hide if fewer than 2 selectable options and no active selection
  const hasSelection = lifecyclePhases.length > 0
  if (!hasMeaningfulOptions(valuesToRender, counts, !!facets) && !hasSelection) return null

  return (
    <FilterSection title="Levenscyclus">
      <div className="flex flex-col gap-1.5">
        {valuesToRender.map((value) => {
          const label = LIFECYCLE_LABEL[value] ?? value
          const count = facets ? (counts.get(value) ?? 0) : undefined
          const checked = lifecyclePhases.includes(value)
          const disabled = facets ? count === 0 && !checked : false
          return (
            <OptionCheckbox
              key={value}
              label={label}
              count={count}
              checked={checked}
              disabled={disabled}
              onToggle={() => toggleMulti('lifecycle_phase', value)}
            />
          )
        })}
      </div>
    </FilterSection>
  )
}

/**
 * 3-state legacy (uitloop) visibility filter. Unlike the other sections this
 * is NOT a facet over the product set — it's an additive visibility toggle
 * (product-merchandising feature 2): by default the catalog shows everything
 * (legacy products stay SEO-visible, WEB-426), but a buyer can hide uitloop
 * hardware or show only uitloop. Wired through the shared `legacy` URL param
 * via `setParam` ('all' clears the param).
 */
function LegacySection({ ctx }: { ctx: RenderContext }) {
  const { includeDiscontinued, setParam } = ctx
  // T0.3: single "Toon uitgefaseerde producten" toggle, default off. Under the
  // relevance-sort flag the catalog hides discontinued/uitloop by default (kept
  // findable for SEO, WEB-426); ticking this sends includeDiscontinued=true.
  return (
    <FilterSection title="Uitloop / einde levensduur">
      <label
        htmlFor="include-discontinued"
        className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer"
      >
        <input
          id="include-discontinued"
          type="checkbox"
          checked={includeDiscontinued}
          onChange={() => setParam('includeDiscontinued', includeDiscontinued ? '' : 'true')}
          className="h-4 w-4 rounded text-primary focus:ring-primary"
        />
        Toon uitgefaseerde producten
      </label>
    </FilterSection>
  )
}

function LabelsSection({ ctx }: { ctx: RenderContext }) {
  const { facets, selectedLabels, toggleMulti } = ctx
  const buckets = facets?.labels ?? []
  if (buckets.length === 0 && selectedLabels.length === 0) return null
  const extraSelected = selectedLabels.filter(
    (l) => !buckets.some((b) => b.value === l),
  )
  const visible: FacetBucket[] = [
    ...buckets.slice(0, 15),
    ...extraSelected.map((value) => ({ value, count: 0 })),
  ]
  return (
    <FilterSection title="Labels">
      <div className="flex flex-wrap gap-1.5">
        {visible.map((b) => {
          const active = selectedLabels.includes(b.value)
          const disabled = facets ? b.count === 0 && !active : false
          return (
            <button
              key={b.value}
              disabled={disabled}
              onClick={() => toggleMulti('labels', b.value)}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${active ? 'bg-primary text-white border-primary' : disabled ? 'bg-neutral-50 text-neutral-300 border-neutral-100 cursor-not-allowed' : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300'}`}
            >
              {b.value}
              <span
                className={
                  active ? 'text-white' : disabled ? 'text-neutral-300' : 'text-neutral-400'
                }
              >
                {b.count}
              </span>
            </button>
          )
        })}
      </div>
    </FilterSection>
  )
}
